import React, { useEffect, useState, useCallback } from "react";
import Modal from "react-modal";
import DarkBlueButton from "../../atoms/buttons/DarkBlueButton";
import WhiteButton from "../../atoms/buttons/WhiteButton";
import CloseButton from "../../atoms/buttons/CloseButton";
import { useNavigate } from "react-router-dom";
import { useHospDocData } from "../../../lib/contexts/HospitalDoctorContext";
import { MdCheckCircle, MdError, MdPending, MdDownload } from "react-icons/md";
import { BiLoaderAlt } from "react-icons/bi";
import { FiClock } from "react-icons/fi";
import {
  getDownloadRequestDetails,
  getDownloadSignedUrl,
} from "../../../lib/apis/analytics";
import {
  DownloadRequestResponse,
  DownloadRequest,
} from "../../../lib/utils/types";
import { format } from "date-fns";
import { toast } from "react-toastify";

interface DownloadRequestModalProps {
  closeModal: () => void;
  modalIsOpen: boolean;
  customStyles: any;
  apiResponse: DownloadRequestResponse | null;
}

type Step = 1 | 2 | 3;

const DownloadRequestModal = ({
  closeModal,
  modalIsOpen,
  customStyles,
  apiResponse,
}: DownloadRequestModalProps) => {
  const navigate = useNavigate();
  const { hospitalID } = useHospDocData();
  
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [downloadDetails, setDownloadDetails] = useState<DownloadRequest | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showConnectingLine, setShowConnectingLine] = useState(false);

  // Reset state when modal opens or closes
  useEffect(() => {
    if (modalIsOpen && apiResponse) {
      setCurrentStep(1);
      setDownloadDetails(null);
      setErrorMessage(null);
      setIsDownloading(false);
      setShowConnectingLine(false);
    }
  }, [modalIsOpen, apiResponse]);

  const fetchDownloadDetails = useCallback(async () => {
    if (!apiResponse?.result?.analytics_downloads_id) return;

    try {
      const details = await getDownloadRequestDetails(apiResponse.result.analytics_downloads_id);
      setDownloadDetails(details);
      setCurrentStep(3);
    } catch (error: any) {
      console.error('Error fetching download details:', error);
      setErrorMessage(error?.message || 'Failed to fetch download details');
      setCurrentStep(3);
    }
  }, [apiResponse]);

  // Progressive step flow
  useEffect(() => {
    if (!modalIsOpen || !apiResponse) return;

    // Step 1: Show queue status for 1 second
    if (currentStep === 1) {
      const timer = setTimeout(() => {
        setShowConnectingLine(true);
        // Wait for line animation, then show step 2
        setTimeout(() => {
          setCurrentStep(2);
        }, 300);
      }, 1000);
      return () => clearTimeout(timer);
    }

    // Step 2: Show processing spinner for 4 seconds
    if (currentStep === 2) {
      const timer = setTimeout(() => {
        fetchDownloadDetails();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, modalIsOpen, apiResponse, fetchDownloadDetails]);

  const handleGoToDownloadCenter = () => {
    if (hospitalID) {
      navigate(`/${hospitalID}/download-center`);
    }
    closeModal();
  };

  const handleDownloadNow = async () => {
    // Get download ID - try multiple possible field names
    const downloadId = downloadDetails?.analytics_downloads_id || 
                       (downloadDetails as any)?.id || 
                       apiResponse?.result?.analytics_downloads_id;
    
    
    if (!downloadId) {
      console.error('No download ID found in:', { downloadDetails, apiResponse });
      toast.error('Download ID not available. Please try again from Download Center.');
      return;
    }

    setIsDownloading(true);
    try {
      const signedUrlResponse = await getDownloadSignedUrl(downloadId);
      
      // Trigger download
      const link = document.createElement('a');
      link.href = signedUrlResponse.downloadUrl;
      const filename = `analytics_${downloadDetails?.analytics_type || 'data'}_${downloadDetails?.start_date || 'export'}_${downloadDetails?.end_date || new Date().toISOString().split('T')[0]}.xlsx`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Download started successfully!');
      
      // Close modal after a short delay
      setTimeout(() => {
        closeModal();
      }, 1000);
    } catch (error: any) {
      console.error('Error downloading file:', error);
      toast.error(error?.message || 'Failed to download file');
      setIsDownloading(false);
    }
  };

  const formatAnalyticsType = (type: string | undefined) => {
    if (!type) return 'Analytics Data';
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      processed: { color: 'bg-green-100 text-green-800', label: 'Processed' },
      processing: { color: 'bg-blue-100 text-blue-800', label: 'Processing' },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      failed: { color: 'bg-red-100 text-red-800', label: 'Failed' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const isProcessed = downloadDetails?.status === 'processed';
  const isFailed = downloadDetails?.status === 'failed';
  const isPendingOrProcessing = downloadDetails?.status === 'pending' || downloadDetails?.status === 'processing';

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      style={customStyles}
      ariaHideApp={false}
    >
      <div className="flex flex-col max-w-[95vw] sm:max-w-[500px] md:max-w-[600px] mx-auto">
        {/* Header */}
        <div className="px-6 py-4 flex flex-row items-center justify-between border-b border-gray-200">
          <p className="text-dark text-lg font-semibold">Download Request</p>
          <CloseButton handleClick={closeModal} />
        </div>

        {/* Scrollable Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Warning Banner - Show during processing */}
          {currentStep < 3 && (
            <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2 animate-fade-in">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-yellow-800 mb-1">Please Wait</p>
                <p className="text-xs text-yellow-700">
                  Your request is being processed. Please do not close this window or press the back button.
                </p>
              </div>
            </div>
          )}

          {/* Step 1: Queue Status */}
          <div className="flex items-start gap-3 mb-3">
            <div className="flex flex-col items-center">
              <div className="bg-green-100 rounded-full p-2.5 flex items-center justify-center w-10 h-10">
                <MdCheckCircle className="w-5 h-5 text-green-600" />
              </div>
              {showConnectingLine && (
                <div className="w-0.5 h-8 bg-gradient-to-b from-green-500 to-blue-500 mt-1.5 animate-grow-down" />
              )}
            </div>
            <div className="flex-1 pt-1">
              <h3 className="text-sm font-semibold text-gray-800 mb-0.5">
                Step 1: Request Accepted
              </h3>
              <p className="text-xs text-gray-600">
                {apiResponse?.message || "Your analytics download request has been queued."}
              </p>
            </div>
          </div>

          {/* Step 2: Processing (appears after Step 1) */}
          {currentStep >= 2 && (
            <div className="flex items-start gap-3 mb-3 animate-fade-in">
              <div className="flex flex-col items-center">
                <div className={`${currentStep === 2 ? 'bg-blue-100' : 'bg-gray-100'} rounded-full p-2.5 flex items-center justify-center w-10 h-10`}>
                  {currentStep === 2 ? (
                    <BiLoaderAlt className="w-5 h-5 text-blue-600 animate-spin" />
                  ) : (
                    <FiClock className="w-5 h-5 text-gray-600" />
                  )}
                </div>
                {currentStep >= 2 && currentStep < 3 && (
                  <div className="w-0.5 h-8 bg-gradient-to-b from-blue-500 to-purple-500 mt-1.5 animate-grow-down" />
                )}
                {currentStep === 3 && (
                  <div className="w-0.5 h-8 bg-gray-300 mt-1.5" />
                )}
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-sm font-semibold text-gray-800 mb-0.5">
                  Step 2: Processing Request
                </h3>
                <p className="text-xs text-gray-600">
                  {currentStep === 2 
                    ? "Preparing your analytics file..."
                    : "Processing completed"}
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Results (appears after Step 2) */}
          {currentStep === 3 && (
            <div className="flex items-start gap-3 mb-3 animate-fade-in">
              <div className="flex flex-col items-center">
                <div className={`${isProcessed ? 'bg-green-100' : isFailed ? 'bg-red-100' : 'bg-yellow-100'} rounded-full p-2.5 flex items-center justify-center w-10 h-10`}>
                  {isProcessed && <MdDownload className="w-5 h-5 text-green-600" />}
                  {isFailed && <MdError className="w-5 h-5 text-red-600" />}
                  {isPendingOrProcessing && <MdPending className="w-5 h-5 text-yellow-600" />}
                  {!downloadDetails && !errorMessage && <BiLoaderAlt className="w-5 h-5 text-gray-600 animate-spin" />}
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  {isProcessed && 'Step 3: Download Ready!'}
                  {isFailed && 'Step 3: Download Failed'}
                  {isPendingOrProcessing && 'Step 3: Still Processing'}
                  {!downloadDetails && !errorMessage && 'Step 3: Fetching Status...'}
                  {errorMessage && 'Step 3: Error Occurred'}
                </h3>

                {/* Error State */}
                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                    <p className="text-xs text-red-600">{errorMessage}</p>
                  </div>
                )}

                {/* Download Details Card - More Compact */}
                {downloadDetails && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-gray-700">Download Details</h4>
                      {getStatusBadge(downloadDetails.status)}
                    </div>
                    
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-center py-1.5 border-b border-gray-200">
                        <span className="text-xs text-gray-500">Type</span>
                        <span className="text-xs font-medium text-gray-900">
                          {formatAnalyticsType(downloadDetails.analytics_type)}
                        </span>
                      </div>
                      
                      {downloadDetails.start_date && (
                        <div className="flex justify-between items-center py-1.5 border-b border-gray-200">
                          <span className="text-xs text-gray-500">Start Date</span>
                          <span className="text-xs font-medium text-gray-900">
                            {formatDate(downloadDetails.start_date)}
                          </span>
                        </div>
                      )}
                      
                      {downloadDetails.end_date && (
                        <div className="flex justify-between items-center py-1.5 border-b border-gray-200">
                          <span className="text-xs text-gray-500">End Date</span>
                          <span className="text-xs font-medium text-gray-900">
                            {formatDate(downloadDetails.end_date)}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center py-1.5">
                        <span className="text-xs text-gray-500">Created</span>
                        <span className="text-xs font-medium text-gray-900">
                          {formatDate(downloadDetails.created_at)}
                        </span>
                      </div>

                      {/* Failure Reason */}
                      {isFailed && downloadDetails.failure_reason && (
                        <div className="mt-2 pt-2 border-t border-red-200 bg-red-50 rounded p-2">
                          <p className="text-xs text-gray-500 mb-1">Failure Reason</p>
                          <p className="text-xs text-red-600 font-medium">
                            {downloadDetails.failure_reason}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Instruction message for pending/processing */}
                {isPendingOrProcessing && downloadDetails && (
                  <p className="text-xs text-gray-600 mt-2">
                    Your file is still being processed. Visit the Download Center to track progress.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons - Fixed at Bottom */}
        <div className="px-6 pb-6 space-y-2 border-t border-gray-200 pt-4">
          {currentStep === 3 && downloadDetails && (
            <>
              {isProcessed && (
                <>
                  <DarkBlueButton
                    name={isDownloading ? "Downloading..." : "Download Now"}
                    handleClick={handleDownloadNow}
                    className="w-full h-11 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
                    disabled={isDownloading}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <WhiteButton
                      name="Download Center"
                      handleClick={handleGoToDownloadCenter}
                      className="w-full h-11 transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
                    />
                    <WhiteButton
                      name="Close"
                      handleClick={closeModal}
                      className="w-full h-11 transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
                    />
                  </div>
                </>
              )}

              {(isFailed || isPendingOrProcessing) && (
                <div className="grid grid-cols-2 gap-2">
                  <DarkBlueButton
                    name="Download Center"
                    handleClick={handleGoToDownloadCenter}
                    className="w-full h-11 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
                  />
                  <WhiteButton
                    name="Close"
                    handleClick={closeModal}
                    className="w-full h-11 transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
                  />
                </div>
              )}
            </>
          )}

          {(currentStep === 3 && errorMessage) && (
            <div className="grid grid-cols-2 gap-2">
              <DarkBlueButton
                name="Download Center"
                handleClick={handleGoToDownloadCenter}
                className="w-full h-11 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
              />
              <WhiteButton
                name="Close"
                handleClick={closeModal}
                className="w-full h-11 transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DownloadRequestModal;
